// @ts-strict-ignore
import { ApplyTextToTextureAreaUsageQuickFixActionVO, ArticleDescriptorVO, DeleteArticleQuickFixActionVO, InsertArticleQuickFixActionVO, ListArticleDescriptorVO, QuickFixActionVO, QuickFixVO, RuleResultVO, SingleArticleDescriptorVO, ValidationResultVO } from '@project/model/vo/validation-context.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ProjectHelper } from '@project/helper/project.helper';
import { SelectionArticleGroupIdentity } from '@project/components/article/model/vo/selection-article-group-identity';
import { QuickFixUtils } from '@shared/util/quick-fix.utils';
import { FavoriteArticlesFilter } from '@project/components/article/presenter/favorite-articles-filter';
import { ArticleLocationVO } from '@article/model/vo/article-location.vo';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { AlertHelper } from '../alert/alert.helper';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { UserModel } from '@user/model/user.model';
import { Injectable } from '@angular/core';
import { ArticleTypeManager } from '@project/components/article/manager/article-type.manager';
import { InsertArticleService } from '@project/components/article/service/insert-article.service';
import { InsertedArticleData } from '@project/components/article/processor/i-article-location.processor';
import { MetaElementUsageIdentityVO, TextValueVO } from '../vo/meta-element.vo';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { UndoApplyTextOperation } from '../undo/operation/undo-apply-text-operation';
import { Log } from '@shared/debug/log';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { RemoveProjectArticleService } from '@project/service/remove-project-article.service';
import { ProjectComponentPresenter } from '@project/components/project-component.presenter';

@Injectable({ providedIn: 'root' })
export class QuickFixManager {
  private static readonly CONTINUE: number = 0;
  private static readonly ABORT: number = 1;
  public static readonly SKIP: number = 2;

  public favoriteArticlesFilter: FavoriteArticlesFilter;

  private _executionInsertAction: boolean = false;

  constructor(
    userModel: UserModel,
    articleTypeManager: ArticleTypeManager,
    private projectModel: ProjectModel,
    private removeProjectArticleService: RemoveProjectArticleService,
    private projectHelper: ProjectHelper,
    private undoRedoManager: UndoRedoManager,
    private undoRedoHelper: UndoRedoHelper,
    private alertHelper: AlertHelper,
    private selectionModel: SelectionModel,
    private selectionModelService: SelectionModelService,
    private undoRedoUtils: UndoRedoUtils,
    private _insertArticleService: InsertArticleService,
    private _articleContainerUtils: ArticleContainerUtils,
    private projectComponentPresenter: ProjectComponentPresenter,
  ) {
    this.favoriteArticlesFilter = new FavoriteArticlesFilter(userModel, articleTypeManager)
  }

  public getSelectedQuickFixes(ruleResults: RuleResultVO[]): QuickFixVO[] {
    if (ArrayUtils.isEmpty(ruleResults)) {
      return [];
    }
    const result: QuickFixVO[] = [];
    for (let i = 0; i < ruleResults.length; i++) {
      const ruleResult: RuleResultVO = ruleResults[i];
      const selectedQuickFix: QuickFixVO = ruleResult.getSelectedQuickFix();
      if (selectedQuickFix) {
        result.push(selectedQuickFix);
      }
    }
    return result;
  }

  public async executeQuickFixes(quickFixes: QuickFixVO[], force: boolean = false): Promise<boolean> {
    this.favoriteArticlesFilter.updateFavoriteArticles();
    const sortedQuickFixes: QuickFixVO[] = ArrayUtils.isEmpty(quickFixes) ? null : quickFixes.concat();
    if (ArrayUtils.isNotEmpty(sortedQuickFixes)) {
      sortedQuickFixes.sort((a: QuickFixVO, b: QuickFixVO) => {
        const aExecPriority: number = a.execPriority;
        const bExecPriority: number = b.execPriority;
        if (aExecPriority > bExecPriority) {
          return -1;
        } else if (aExecPriority < bExecPriority) {
          return 1;
        } else {
          return 0;
        }
      });
    }

    return this.executeSortedQuickFixes(sortedQuickFixes, force);
  }

  private async executeSortedQuickFixes(sortedQuickFixes: QuickFixVO[], force: boolean, index: number = 0): Promise<boolean> {
    if (sortedQuickFixes && index < sortedQuickFixes.length) {
      const quickFix: QuickFixVO = sortedQuickFixes[index];
      const aborted = await this.executeQuickFix(quickFix, force);
      if (aborted) {
        return aborted;
      } else {
        index++;
        await this.executeSortedQuickFixes(sortedQuickFixes, force, index);
      }
    } else {
      return false;
    }
  }

  private async executeQuickFix(quickFix: QuickFixVO, force: boolean): Promise<boolean> {
    if (!quickFix) {
      return false;
    }

    const actions: QuickFixActionVO[] = quickFix.actions;
    if (ArrayUtils.isEmpty(actions)) {
      return false;
    }

    return this.executeQuickFixActions(actions, quickFix, force);
  }

  private async executeQuickFixActions(actions: QuickFixActionVO[], quickFix: QuickFixVO, force: boolean, index: number = 0): Promise<boolean> {
    if (index < actions.length) {
      const action: QuickFixActionVO = actions[index];
      const status = await this.executeQuickFixAction(action, quickFix, index, force);
      if (status === QuickFixManager.ABORT) {
        return true;
      } else if (status === QuickFixManager.SKIP) {
        return false;
      } else {
        index++;
        await this.executeQuickFixActions(actions, quickFix, force, index);
      }
    } else {
      return false;
    }
  }

  private async executeQuickFixAction(action: QuickFixActionVO, quickFix: QuickFixVO, actionPos: number, force: boolean): Promise<number> {
    const operatingLocationArticleUsageId: string = QuickFixUtils.getOperatingLocationOfQuickFixActionAsExistingArticleUsageId(action);

    if (operatingLocationArticleUsageId && !force) {
      const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(operatingLocationArticleUsageId);
      if (articleUsage) {
        const selectionGroupIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(articleUsage);
        this.selectionModelService.selectObject(selectionGroupIdentity, false, false);
        this.selectionModel.viewFirstSelected();
      }
    }
    if (action instanceof DeleteArticleQuickFixActionVO) {
      const deleteArticleQuickFixAction: DeleteArticleQuickFixActionVO = action;
      await this.detachArticlesByDescriptor(deleteArticleQuickFixAction.articleDescriptor, deleteArticleQuickFixAction.closeGap,
        deleteArticleQuickFixAction.gapLeftArticleDescriptor, deleteArticleQuickFixAction.gapRightArticleDescriptor);
      return QuickFixManager.CONTINUE;
    } else if (action instanceof ApplyTextToTextureAreaUsageQuickFixActionVO) {
      const applyTextToTextureAreaUsageQuickFixActionVO: ApplyTextToTextureAreaUsageQuickFixActionVO = action;
      await this.applyTextToTextureAreaUsage(applyTextToTextureAreaUsageQuickFixActionVO.textureAreaUsageIdentity,
        applyTextToTextureAreaUsageQuickFixActionVO.textValue, true);
      return QuickFixManager.CONTINUE;
    } else if (action instanceof InsertArticleQuickFixActionVO) {
      const insertArticleQuickFixAction: InsertArticleQuickFixActionVO = action;
      const articleDescriptor: ArticleDescriptorVO = insertArticleQuickFixAction.articleDescriptor;
      let article: ArticleVO = null;
      if (articleDescriptor instanceof ListArticleDescriptorVO) {
        const listArticleDescriptor: ListArticleDescriptorVO = articleDescriptor;
        const availableArticles: ArticleVO[] = listArticleDescriptor.articles;

        if (availableArticles && (availableArticles.length === 1 || force)) {
          return this.executeInsertQuickFixAction(availableArticles[0], insertArticleQuickFixAction.articleLocation);
        } else if (!force) {
          const result = await this.projectComponentPresenter.openSelectArticlePopup(availableArticles, 'selectArticlePopup.insert.title', quickFix.message, false);
          if (result) {
            if (result instanceof ArticleVO) {
              return this.executeInsertQuickFixAction(result, insertArticleQuickFixAction.articleLocation);
            } else {
              await this.undoQuickfix(actionPos);
              return QuickFixManager.SKIP;
            }
          } else {
            await this.undoQuickfix(actionPos);
            return QuickFixManager.ABORT;
          }
        } else {
          return QuickFixManager.SKIP;
        }
      } else if (articleDescriptor instanceof SingleArticleDescriptorVO) {
        article = articleDescriptor.article;
        return this.executeInsertQuickFixAction(article, insertArticleQuickFixAction.articleLocation);
      } else {
        return QuickFixManager.SKIP;
      }
    } else {
      return QuickFixManager.SKIP;
    }
  }

  protected async undoQuickfix(stepCount: number): Promise<void> {
    let i: number;
    for (i = 0; i < stepCount; i++) {
      await this.undoRedoManager.undo();
    }
  }

  private executeInsertQuickFixAction(article: ArticleVO, articleLocation: ArticleLocationVO): Promise<number> {
    if (!article) {
      return Promise.reject(new Error("Article is not defined"));
    }
    this._executionInsertAction = true;
    articleLocation.checkConnectionProperties = true;
    return this.insertArticleByLocation(article, articleLocation);
  }

  private async insertArticleByLocation(article: ArticleVO, articleLocation: ArticleLocationVO): Promise<number> {
    try {
      const insertedArticleData = await this._insertArticleService.insertArticleByArticleLocation(article, articleLocation)
      return this.insertArticleByLocation_success(insertedArticleData);
    } catch (error) {
      Log.error('Could not insert article: ', error);
      return this.insertArticleByLocation_failure(new Error('Could not insert article'));
    }
  }

  private async detachArticlesByDescriptor(articleDescriptor: ArticleDescriptorVO, closeGap: boolean,
    gapLeftArticleDescriptor: ArticleDescriptorVO, gapRightArticleDescriptor: ArticleDescriptorVO): Promise<void> {
    await this.removeProjectArticleService.detachArticleUsagesFromProjectByArticleDescriptor(articleDescriptor, closeGap, gapLeftArticleDescriptor,
      gapRightArticleDescriptor);
  }

  protected async insertArticleByLocation_success(insertedArticleData: InsertedArticleData): Promise<number> {
    if (!this._executionInsertAction) {
      return;
    }
    if (insertedArticleData.insertedArticleGUIDs && insertedArticleData.insertedArticleGUIDs.length > 0 && insertedArticleData.createUndoOperation) {
      this.undoRedoHelper.pushInsertArticleByLocationUndoOperation(insertedArticleData);
    }
    this._executionInsertAction = false;
    return QuickFixManager.CONTINUE;
  }

  protected insertArticleByLocation_failure(data: Error): number {
    if (!this._executionInsertAction) {
      return;
    }
    this._executionInsertAction = false;
    this.alertHelper.showError('global.title.error', data.message || 'global.message.unexpected_error');

    // failing to connect article is like aborting to connect that article, so basically quickfix was not completed
    // and thus (if used) automatic restart of quickfixing newly found errors must stop,
    // otherwise its infinite loop user cannot escape from
    return QuickFixManager.ABORT;
  }

  public existsProblemsWithExecutableSelectedActions(validationResult: ValidationResultVO): boolean {
    if (!validationResult) {
      return false;
    }
    if (ArrayUtils.isEmpty(validationResult.ruleResults)) {
      return false;
    }
    for (const ruleResult of validationResult.ruleResults) {
      const selectedQuickFix: QuickFixVO = ruleResult.getSelectedQuickFix();
      if (selectedQuickFix && ArrayUtils.isNotEmpty(selectedQuickFix.actions)) {
        return true;
      }
    }
    return false;
  }

  public existsProblems(validationResult: ValidationResultVO): boolean {
    if (!validationResult) {
      return false;
    }
    if (ArrayUtils.isEmpty(validationResult.ruleResults)) {
      return false;
    }
    return true;
  }

  private async applyTextToTextureAreaUsage(textureAreaUsageIdentity: MetaElementUsageIdentityVO, textValue: TextValueVO,
                                     _applySceneTextColor: boolean): Promise<void> {
    const originalTextValue: TextValueVO = this._articleContainerUtils.applyTextToTextureAreaUsage(textureAreaUsageIdentity, textValue);
    const undoApplyTextOperation: UndoApplyTextOperation = this.undoRedoUtils.createUndoApplyTextOperationByTextureAreaUsageIdentity(
      textureAreaUsageIdentity, originalTextValue, textValue);
    if (undoApplyTextOperation) {
      await this.undoRedoHelper.pushUndoOperation(undoApplyTextOperation);
    }
  }

  public async projectValidatedSuccessHandler(validationResult: ValidationResultVO): Promise<boolean> {
    if (validationResult && this.existsProblemsWithExecutableSelectedActions(validationResult)) {
      return this.executeQuickFixesInternal(validationResult);
    } else {
      return true;
    }
  }

  private async executeQuickFixesInternal(validationResult: ValidationResultVO): Promise<boolean> {
    if (this.existsProblems(validationResult)) {
      try {
        return this.executeQuickFixes(this.getSelectedQuickFixes(validationResult.ruleResults), true);
      } catch (error) {
        return false;
      }
    } else {
      return true;
    }
  }
}
